import { Area, AreaChart, Legend, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import CumulativeChartTooltip from './cumulative-chart-tooltip'
import CumulativeChartLegend from './cumulative-chart-legend'
import { useApiGetYearlyCumulativeGmvChartData } from '../../../../core/api/home/useApiGetYearlyCumulativeGmvChartData'
import { useContext } from 'react'
import { HomeContext } from '../../state/home-context'
import { VpLoader } from '@vtmn-play/react'
import WidgetErrorCard from 'components/common/cards/widget-error-card'
import { isHomeApiEnabled } from 'core/api/home/home-api-utils'

const getYesterdayDayOfYearIndex = () => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  // Chart data is for the current calendar year only — on Jan 1, yesterday is Dec 31 of last year.
  if (yesterday.getFullYear() !== today.getFullYear()) {
    return undefined
  }

  const start = new Date(yesterday.getFullYear(), 0, 0)
  const diff =
    yesterday.getTime() - start.getTime() + (start.getTimezoneOffset() - yesterday.getTimezoneOffset()) * 60 * 1000

  // Day-of-year is 1-based; chart data is 0-based (Jan 1 = index 0).
  return Math.floor(diff / (1000 * 60 * 60 * 24)) - 1
}

const CumulativeChart = () => {
  const { aggregation, teamId, channel, storeId } = useContext(HomeContext)

  const { data, isLoading, isError } = useApiGetYearlyCumulativeGmvChartData(
    { aggregation, teamId, channel, storeId },
    isHomeApiEnabled(storeId, teamId, aggregation)
  )

  const hasChartData = (data?.length ?? 0) > 0
  const showErrorCard = isError || !hasChartData

  return (
    <div className="mg-cumulative-chart" data-testid="mg-cumulative-chart">
      {isLoading && (
        <div className="mg-cumulative-chart-loading">
          <VpLoader {...({ size: 'extra-large' } as any)} />
        </div>
      )}

      {!isLoading && showErrorCard && (
        <WidgetErrorCard titleKey="general.cumulative_charts.error-title" testId="cumulative-chart-error" />
      )}

      {!isLoading && hasChartData && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="gradient" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--vp-semantic-color-content-brand)" stopOpacity={1} />
                <stop offset="100%" stopColor="var(--vp-semantic-color-content-brand)" stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <XAxis dataKey="date" tickLine={false} axisLine={false} hide={true} />

            <Tooltip content={CumulativeChartTooltip} defaultIndex={getYesterdayDayOfYearIndex()} />

            <Area
              dataKey="realizedGmv"
              type="linear"
              stroke="var(--vp-semantic-color-status-warning)"
              fill="white"
              strokeWidth={2}
            />
            <Area
              dataKey="forecastGmv"
              type="linear"
              stroke="var(--vp-semantic-color-content-quiet)"
              fill="white"
              strokeWidth={2}
              strokeDasharray="5 4"
            />
            <Area
              dataKey="trajectoryGmv"
              type="linear"
              stroke="var(--vp-semantic-color-content-brand)"
              fill={'url(#gradient)'}
              strokeWidth={0}
            />

            <Legend verticalAlign="bottom" align="left" content={CumulativeChartLegend} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export default CumulativeChart
